import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import { Add, Edit, Delete, Visibility, PersonAdd, Warning } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { getUsers, createAuthUserForCompany } from '../lib/api';
import DetailDialog from '../components/DetailDialog';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { FilterBar, FilterConfig } from '../components/common';

const CompaniesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [contractors, setContractors] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [classifications, setClassifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [classificationsLoading, setClassificationsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedClassifications, setSelectedClassifications] = useState<string[]>([]);
  const [hasAuthUsersFilter, setHasAuthUsersFilter] = useState('all');
  const [classificationStatusFilter, setClassificationStatusFilter] = useState('all');
  const [authUsers, setAuthUsers] = useState<any[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | number | null>(null);
  const [createAuthUserDialogOpen, setCreateAuthUserDialogOpen] = useState(false);
  const [selectedCompanyForAuth, setSelectedCompanyForAuth] = useState<any | null>(null);
  const [creatingAuthUser, setCreatingAuthUser] = useState(false);
  const [authUserCreated, setAuthUserCreated] = useState<{ email: string; password: string } | null>(null);

  useEffect(() => {
    fetchContractors();
    fetchCities();
    fetchClassifications();
    fetchAuthUsers();
  }, []);

  const fetchAuthUsers = async () => {
    try {
      const users = await getUsers();
      setAuthUsers(users || []);
    } catch (error) {
      console.error('Error fetching auth users:', error);
      setAuthUsers([]);
    }
  };

  const fetchClassifications = async () => {
    try {
      const { data, error } = await supabase
        .from('classifications')
        .select('id, category, field, specialization')
        .order('category');
      
      if (error) throw error;
      setClassifications(data || []);
    } catch (error) {
      console.error('Error fetching classifications:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const { data, error } = await supabase
        .from('cities')
        .select(`
          id,
          city,
          translation,
          area_id,
          areas:area_id (
            id,
            area,
            translation
          )
        `)
        .order('city');
      
      if (error) throw error;
      setCities(data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchContractors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          *,
          cities:city_id (
            city,
            translation,
            areas:area_id (
              area,
              translation
            )
          )
        `)
        .order('id', { ascending: false });
      
      if (error) throw error;
      
      // Set companies without classifications first for fast initial load
      setContractors((data || []).map(company => ({ ...company, classifications: [] })));
      setLoading(false);
      
      // Then fetch classifications in the background
      setClassificationsLoading(true);
      const companiesWithClassifications = await Promise.all(
        (data || []).map(async (company) => {
          const classificationData = company.classifications || company.classification_id;
          
          if (classificationData) {
            let classIds;
            if (typeof classificationData === 'string') {
              try {
                classIds = JSON.parse(classificationData);
              } catch (e) {
                classIds = [classificationData];
              }
            } else if (Array.isArray(classificationData)) {
              classIds = classificationData;
            } else {
              classIds = [classificationData];
            }
            
            const validClassIds = classIds.filter((id: any) => id != null && id !== '');
            
            if (validClassIds.length > 0) {
              const { data: classificationsData, error: classError } = await supabase
                .from('classifications')
                .select('id, category, field, specialization')
                .in('id', validClassIds);
              
              if (classError) {
                console.error('Error fetching classifications:', classError);
                return { ...company, classifications: [] };
              }
              
              return { ...company, classifications: classificationsData || [] };
            }
          }
          return { ...company, classifications: [] };
        })
      );
      
      setContractors(companiesWithClassifications);
      setClassificationsLoading(false);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setLoading(false);
      setClassificationsLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedRecordId(null);
    setDetailDialogOpen(true);
  };

  const handleView = (id: string | number) => {
    navigate(`/companies/${id}`);
  };

  const handleEdit = (id: string | number) => {
    navigate(`/companies/${id}?edit=true`);
  };

  const handleDelete = (id: string | number) => {
    setSelectedRecordId(id);
    setDeleteDialogOpen(true);
  };

  const handleCreateAuthUser = (company: any) => {
    setSelectedCompanyForAuth(company);
    setCreateAuthUserDialogOpen(true);
    setAuthUserCreated(null);
  };

  const handleConfirmCreateAuthUser = async () => {
    if (!selectedCompanyForAuth) return;
    
    setCreatingAuthUser(true);
    try {
      const result = await createAuthUserForCompany(selectedCompanyForAuth.id);
      setAuthUserCreated({
        email: selectedCompanyForAuth.email,
        password: result.tempPassword
      });
      // Refresh auth users list
      await fetchAuthUsers();
    } catch (error: any) {
      console.error('Error creating auth user:', error);
      alert(error.message || 'Failed to create auth user');
      setCreateAuthUserDialogOpen(false);
    } finally {
      setCreatingAuthUser(false);
    }
  };

  const handleCloseAuthUserDialog = () => {
    setCreateAuthUserDialogOpen(false);
    setSelectedCompanyForAuth(null);
    setAuthUserCreated(null);
  };

  const contractorFields = [
    { name: 'pcu_id', label: t('companies.pcuId'), type: 'text' as const, required: true },
    { name: 'name', label: t('companies.name'), type: 'text' as const, required: true },
    { name: 'email', label: t('companies.email'), type: 'email' as const, required: true },
    { name: 'mobile_number', label: t('companies.mobileNumber'), type: 'text' as const },
    { name: 'landline_number', label: t('companies.landlineNumber'), type: 'text' as const },
    { name: 'fax_number', label: t('companies.faxNumber'), type: 'text' as const },
    { 
      name: 'city_id', 
      label: t('companies.city'), 
      type: 'select' as const,
      required: true,
      options: cities.map(city => ({
        value: city.id,
        label: isRTL 
          ? `${city.translation} - ${city.areas?.translation || ''}` 
          : `${city.city} - ${city.areas?.area || ''}`
      }))
    },
    { 
      name: 'classification_id', 
      label: t('companies.classifications'), 
      type: 'multiselect' as const,
      foreignKey: {
        table: 'classifications',
        valueField: 'id',
        labelField: 'category,field,specialization'
      }
    },
    { name: 'classification_expire_date', label: t('companies.expiryDate'), type: 'date' as const },
  ];

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('companies.name'), minWidth: 250, flex: 3, pinnable: true },
    { field: 'email', headerName: t('companies.email'), width: 200 },
    { field: 'mobile_number', headerName: t('companies.mobile'), width: 150 },
    { 
      field: 'location', 
      headerName: t('companies.location'), 
      width: 200,
      valueGetter: (_value, row) => {
        const city = isRTL ? row.cities?.translation : row.cities?.city;
        const area = isRTL ? row.cities?.areas?.translation : row.cities?.areas?.area;
        if (city && area) {
          return `${city} - ${area}`;
        }
        return city || area || '';
      }
    },
    { 
      field: 'classifications', 
      headerName: t('companies.classifications'), 
      width: 300,
      renderCell: (params: GridRenderCellParams) => {
        const classifications = params.row.classifications || [];
        
        // Show loading indicator while classifications are being fetched
        if (classificationsLoading) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {t('common.loading')}...
              </Typography>
            </Box>
          );
        }
        
        // Show classifications as chips
        if (classifications.length === 0) {
          return '-';
        }
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
            {classifications.slice(0, 2).map((c: any, index: number) => (
              <Chip 
                key={`${params.row.id}-class-${c.id || index}`}
                label={`${c.category} - ${c.field}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
            {classifications.length > 2 && (
              <Chip 
                key={`${params.row.id}-more`}
                label={`+${classifications.length - 2}`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Box>
        );
      }
    },
    { 
      field: 'classification_expire_date', 
      headerName: t('companies.expiryDate'), 
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const expiryDate = params.value;
        if (!expiryDate) return '-';
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiry = new Date(expiryDate);
        const isExpired = expiry < today;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: isExpired ? 'error.main' : 'text.primary',
                fontWeight: isExpired ? 600 : 400
              }}
            >
              {expiryDate}
            </Typography>
            {isExpired && (
              <Tooltip title={t('companies.expired')}>
                <Warning color="error" fontSize="small" />
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('table.actions'),
      width: 120,
      getActions: (params) => {
        const company = params.row;
        const hasAuthUser = authUsers.some(user => 
          user.user_metadata?.company_id === company.id
        );
        
        
        const actions = [
          <GridActionsCellItem
            icon={<Visibility />}
            label={t('table.view')}
            onClick={() => handleView(params.id)}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<Edit />}
            label={t('table.edit')}
            onClick={() => handleEdit(params.id)}
            showInMenu
          />,
        ];
        
        // Add "Create Auth User" action if company has email and no auth user yet
        if (company.email && !hasAuthUser) {
          actions.push(
            <GridActionsCellItem
              icon={<PersonAdd />}
              label={t('companies.createAuthUser') || 'Create Auth User'}
              onClick={() => handleCreateAuthUser(company)}
              showInMenu
            />
          );
        }
        
        actions.push(
          <GridActionsCellItem
            icon={<Delete />}
            label={t('table.delete')}
            onClick={() => handleDelete(params.id)}
            showInMenu
          />
        );
        
        return actions;
      },
    },
  ];

  // Filter contractors based on search text, city, classifications, and auth users
  const filteredContractors = contractors.filter((contractor: any) => {
    // Search filter
    const matchesSearch = searchText === '' || 
      Object.values(contractor).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase())
      );
    
    // City filter - now supports multiple cities
    const matchesCity = selectedCities.length === 0 || selectedCities.includes(contractor.city_id);
    
    // Classifications filter
    const matchesClassifications = selectedClassifications.length === 0 || 
      selectedClassifications.some((classId) => {
        const contractorClassIds = contractor.classifications?.map((c: any) => String(c.id)) || [];
        return contractorClassIds.includes(classId);
      });

    // Auth users filter
    const matchesAuthUsers = (() => {
      if (hasAuthUsersFilter === 'all') return true;
      
      const hasLinkedUsers = authUsers.some(user => 
        user.user_metadata?.company_id === contractor.id
      );
      
      return hasAuthUsersFilter === 'with-users' ? hasLinkedUsers : !hasLinkedUsers;
    })();

    // Classification status filter (expired/active)
    const matchesClassificationStatus = (() => {
      if (classificationStatusFilter === 'all') return true;
      
      const expiryDate = contractor.classification_expire_date;
      if (!expiryDate) {
        // No expiry date - treat as expired if filtering for expired
        return classificationStatusFilter === 'expired';
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(expiryDate);
      const isExpired = expiry < today;
      
      return classificationStatusFilter === 'expired' ? isExpired : !isExpired;
    })();
    
    return matchesSearch && matchesCity && matchesClassifications && matchesAuthUsers && matchesClassificationStatus;
  });

  // Prepare filter configurations
  const filterConfigs: FilterConfig[] = [
    {
      name: 'classificationStatus',
      label: t('companies.classificationStatus') || 'Classification Status',
      options: [
        { value: 'all', label: t('common.all') || 'All' },
        { value: 'active', label: t('companies.active') || 'Active' },
        { value: 'expired', label: t('companies.expired') || 'Expired' },
      ],
      value: classificationStatusFilter,
      onChange: setClassificationStatusFilter,
      multiple: false,
    },
    {
      name: 'hasAuthUsers',
      label: t('companies.hasAuthUsers') || 'Has Auth Users',
      options: [
        { value: 'all', label: t('common.all') || 'All' },
        { value: 'with-users', label: t('companies.withUsers') || 'With Users' },
        { value: 'without-users', label: t('companies.withoutUsers') || 'Without Users' },
      ],
      value: hasAuthUsersFilter,
      onChange: setHasAuthUsersFilter,
      multiple: false,
    },
    {
      name: 'classifications',
      label: t('companies.classifications') || 'Classifications',
      options: classifications.map(classification => ({
        value: String(classification.id),
        label: `${classification.category} - ${classification.field}${classification.specialization ? ` (${classification.specialization})` : ''}`
      })),
      value: selectedClassifications,
      onChange: setSelectedClassifications,
      multiple: true,
    },
    {
      name: 'city',
      label: t('companies.city') || 'City',
      options: cities.map(city => ({
        value: city.id,
        label: isRTL 
          ? `${city.translation} - ${city.areas?.translation || ''}` 
          : `${city.city} - ${city.areas?.area || ''}`
      })),
      value: selectedCities,
      onChange: setSelectedCities,
      multiple: true,
    },
  ];

  return (
    <Box>
      {/* Page Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
              {t('nav.companies')}
            </Typography>
            {/* Filter Stats */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`${t('common.total')}: ${contractors.length}`}
                color="primary"
                variant="outlined"
                size="small"
              />
              {filteredContractors.length !== contractors.length && (
                <Chip
                  label={`${t('common.filtered')}: ${filteredContractors.length}`}
                  color="success"
                  size="small"
                />
              )}
              {selectedCities.length > 0 && (
                <Chip
                  label={`${t('companies.city')}: ${selectedCities.length} ${t('common.selected')}`}
                  onDelete={() => setSelectedCities([])}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {selectedClassifications.length > 0 && (
                <Chip
                  label={`${t('companies.classifications')}: ${selectedClassifications.length} ${t('common.selected')}`}
                  onDelete={() => setSelectedClassifications([])}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
              {hasAuthUsersFilter !== 'all' && (
                <Chip
                  label={`${t('companies.hasAuthUsers')}: ${hasAuthUsersFilter === 'with-users' ? t('companies.withUsers') : t('companies.withoutUsers')}`}
                  onDelete={() => setHasAuthUsersFilter('all')}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              )}
              {classificationStatusFilter !== 'all' && (
                <Chip
                  label={`${t('companies.classificationStatus')}: ${classificationStatusFilter === 'active' ? t('companies.active') : t('companies.expired')}`}
                  onDelete={() => setClassificationStatusFilter('all')}
                  color={classificationStatusFilter === 'expired' ? 'error' : 'success'}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Button variant="contained" onClick={handleAdd} sx={{ gap: 1 }}>
            <Add sx={{ fontSize: 18 }} />
            {t('table.add')}
          </Button>
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('table.search')}
        filters={filterConfigs}
      />

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredContractors}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          density="compact"
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
            },
          }}
          onRowDoubleClick={(params) => handleView(params.id)}
          localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
            '& .MuiDataGrid-columnHeaders': {
              direction: isRTL ? 'rtl' : 'ltr',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              textAlign: 'start',
            },
            '& .MuiDataGrid-cell': {
              py: 0.5,
              textAlign: 'start',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-footerContainer': {
              direction: isRTL ? 'rtl' : 'ltr',
            },
            // Sticky first column (name) — logical property handles RTL automatically
            '& .MuiDataGrid-cell[data-field="name"]': {
              position: 'sticky',
              insetInlineStart: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
            },
            '& .MuiDataGrid-columnHeader[data-field="name"]': {
              position: 'sticky',
              insetInlineStart: 0,
              backgroundColor: 'background.paper',
              zIndex: 2,
            },
            // Sticky last column (actions) — logical property handles RTL automatically
            '& .MuiDataGrid-cell[data-field="actions"]': {
              position: 'sticky',
              insetInlineEnd: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
            },
            '& .MuiDataGrid-columnHeader[data-field="actions"]': {
              position: 'sticky',
              insetInlineEnd: 0,
              backgroundColor: 'background.paper',
              zIndex: 2,
            },
          }}
        />
      </Box>

      {/* Detail Dialog */}
      <DetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        tableName="companies"
        recordId={selectedRecordId || undefined}
        onSave={fetchContractors}
        fields={contractorFields}
      />

      {/* Delete Confirmation Dialog */}
      {selectedRecordId && (
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          tableName="companies"
          recordId={selectedRecordId}
          onDelete={fetchContractors}
        />
      )}

      {/* Create Auth User Dialog */}
      <Dialog open={createAuthUserDialogOpen} onClose={handleCloseAuthUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {authUserCreated 
            ? t('companies.authUserCreatedTitle') || 'Auth User Created Successfully'
            : t('companies.createAuthUser') || 'Create Auth User'
          }
        </DialogTitle>
        <DialogContent>
          {authUserCreated ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('companies.authUserCreatedMessage') || 'Authentication user has been created successfully!'}
              </Alert>
              <DialogContentText>
                <strong>{t('companies.email') || 'Email'}:</strong> {authUserCreated.email}
              </DialogContentText>
              <DialogContentText sx={{ mt: 1 }}>
                <strong>{t('companies.temporaryPassword') || 'Temporary Password'}:</strong> {authUserCreated.password}
              </DialogContentText>
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('companies.passwordWarning') || 'Please save this password securely. It will not be shown again. The user should change this password on first login.'}
              </Alert>
            </>
          ) : (
            <>
              <DialogContentText>
                {t('companies.createAuthUserConfirm') || 'Are you sure you want to create an authentication user for this company?'}
              </DialogContentText>
              {selectedCompanyForAuth && (
                <>
                  <DialogContentText sx={{ mt: 2 }}>
                    <strong>{t('companies.companyName') || 'Company'}:</strong> {selectedCompanyForAuth.name}
                  </DialogContentText>
                  <DialogContentText>
                    <strong>{t('companies.email') || 'Email'}:</strong> {selectedCompanyForAuth.email}
                  </DialogContentText>
                </>
              )}
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('companies.autoPasswordGeneration') || 'A temporary password will be automatically generated for this user.'}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {authUserCreated ? (
            <Button onClick={handleCloseAuthUserDialog} variant="contained">
              {t('common.close') || 'Close'}
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseAuthUserDialog} disabled={creatingAuthUser}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleConfirmCreateAuthUser}
                variant="contained"
                disabled={creatingAuthUser}
                sx={{ gap: 1 }}
              >
                {creatingAuthUser ? <CircularProgress size={20} color="inherit" /> : <PersonAdd sx={{ fontSize: 18 }} />}
                {creatingAuthUser
                  ? (t('common.creating') || 'Creating...')
                  : (t('common.create') || 'Create')
                }
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesPage;
